import type { ApiError, ApiResponse } from '@flymind/shared';

const BASE_URL = import.meta.env.VITE_API_URL || 'https://api.flymind.travel/api';

const TOKEN_KEY = 'flymind_access_token';
const REFRESH_TOKEN_KEY = 'flymind_refresh_token';

// Storage layer with graceful fallbacks. The order is:
//   1. localStorage  (persistent across tabs and reloads)
//   2. sessionStorage (persists for the tab lifetime — survives reloads)
//   3. in-memory     (this page only — at least keeps the user logged in
//                     for the current navigation)
//
// Why this matters on mobile:
//   - Safari iOS in private mode disables localStorage entirely.
//   - In-app browsers (Gmail/Telegram/WhatsApp/Instagram WebViews) often
//     have an isolated or wiped storage.
//   - iOS Intelligent Tracking Prevention can wipe localStorage if the
//     site hasn't been visited for 7 days.
// Without this fallback, calling localStorage.setItem in those contexts
// throws a SecurityError / QuotaExceededError and the entire login flow
// crashes. With it, the user at least stays logged in for the session.

const memoryStore = new Map<string, string>();

function safeGet(key: string): string | null {
  try {
    const v = localStorage.getItem(key);
    if (v != null) return v;
  } catch { /* localStorage blocked */ }
  try {
    const v = sessionStorage.getItem(key);
    if (v != null) return v;
  } catch { /* sessionStorage blocked */ }
  return memoryStore.get(key) ?? null;
}

function safeSet(key: string, value: string): void {
  let stored = false;
  try {
    localStorage.setItem(key, value);
    stored = true;
  } catch {
    try {
      sessionStorage.setItem(key, value);
      stored = true;
    } catch { /* both blocked */ }
  }
  // Always mirror to in-memory so the current page works even if both
  // web storages threw (Safari Private mode + iframe sandbox).
  memoryStore.set(key, value);
  if (!stored) {
    console.warn(
      '[auth] persistent storage blocked, token kept only in memory. ' +
      'You will be logged out on the next page reload — likely due to ' +
      'Safari Private mode or an in-app webview.',
    );
  }
}

function safeRemove(key: string): void {
  try { localStorage.removeItem(key); } catch { /* */ }
  try { sessionStorage.removeItem(key); } catch { /* */ }
  memoryStore.delete(key);
}

export function getAccessToken(): string | null {
  return safeGet(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return safeGet(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  safeSet(TOKEN_KEY, accessToken);
  safeSet(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  safeRemove(TOKEN_KEY);
  safeRemove(REFRESH_TOKEN_KEY);
}

class ApiClientError extends Error {
  constructor(
    public readonly apiError: ApiError,
    public readonly status: number,
  ) {
    super(apiError.error.message);
    this.name = 'ApiClientError';
  }
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      isRefreshing = false;
      refreshPromise = null;
      return false;
    }

    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) {
        clearTokens();
        return false;
      }

      const data = (await res.json()) as ApiResponse<{
        tokens: { accessToken: string; refreshToken: string };
      }>;

      if (data.success) {
        setTokens(data.data.tokens.accessToken, data.data.tokens.refreshToken);
        return true;
      }

      clearTokens();
      return false;
    } catch {
      clearTokens();
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${BASE_URL}${path}`;

  const doFetch = (token: string | null) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let res = await doFetch(getAccessToken());

  // If 401, attempt token refresh and retry once
  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      res = await doFetch(getAccessToken());
    }
  }

  const json = await res.json();

  if (!res.ok || json.success === false) {
    const apiError: ApiError = json.success === false
      ? json
      : {
          success: false as const,
          error: {
            code: `HTTP_${res.status}`,
            message: json.message || res.statusText,
          },
        };
    throw new ApiClientError(apiError, res.status);
  }

  // Return the data payload from ApiResponse<T>
  return (json as ApiResponse<T>).data;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

export { ApiClientError };
